import { BATTERY_FINANCING_LOCKED_MESSAGE } from "~/api/battery-concert-application-errors"
import useBatteryConcertApplication from "~/api/hooks/useBatteryConcertApplication"
import useBatteryProposal from "~/api/hooks/useBatteryProposal"
import useBatteryProposalFinancing from "~/api/hooks/useBatteryProposalFinancing"

import { CONCERT_BATTERY_FINANCE_TYPE } from "./concert-battery-copy"
import { LIGHTREACH_BATTERY_FINANCE_TYPE } from "../../lightreach-battery"
import {
  SUNGAGE_BATTERY_FINANCE_TYPE,
  SUNGAGE_FINANCING_LOCKED_REASON,
} from "../../sungage/sungage-battery-copy"

export type BatteryFinancingLock = {
  /** The server reports financing-affecting edits blocked. */
  locked: boolean
  /** Lock state is not known yet; hold writes. */
  pending: boolean
  reason: string | null
  /** Application status destination for the notice link. */
  statusKind: `contract` | `financing-status`
  /** The lock read has answered for this plan. */
  known: boolean
  /** The last lock read failed; `locked` may be out of date. */
  unavailable: boolean
}

const UNLOCKED: BatteryFinancingLock = {
  locked: false,
  pending: false,
  reason: null,
  statusKind: `financing-status`,
  known: true,
  unavailable: false,
}

/**
 * Server-owned edit lock for equipment, pricing inputs, Finance Plan, and
 * Loan Product controls. Concert uses its application read; Sungage and
 * LightReach use GET financing `financingLocked` / `editLocked`. A caller that
 * needs the lock only some of the time passes `enabled`; while it is false,
 * nothing is read and a plan that locks holds as pending.
 */
export const useBatteryFinancingLock = (
  proposalId: string,
  { enabled = true }: { enabled?: boolean } = {},
): BatteryFinancingLock => {
  const proposalQuery = useBatteryProposal(proposalId)
  const isConcert =
    proposalQuery.data?.financePlan?.financeType === CONCERT_BATTERY_FINANCE_TYPE
  const statusQuery = useBatteryConcertApplication(proposalId, {
    enabled: enabled && isConcert,
  })
  // Sungage and LightReach lock from the financing read, so their writes
  // wait for it rather than run unlocked while it loads.
  const holdsWhileLoading =
    proposalQuery.data?.financePlan?.financeType ===
      SUNGAGE_BATTERY_FINANCE_TYPE ||
    proposalQuery.data?.financePlan?.financeType ===
      LIGHTREACH_BATTERY_FINANCE_TYPE
  const financingQuery = useBatteryProposalFinancing(proposalId, {
    enabled: enabled && !isConcert,
  })

  if (isConcert) {
    const status = statusQuery.data
    const unavailable = statusQuery.isError
    if (status == null) {
      return {
        locked: false,
        pending: !statusQuery.isError,
        reason: null,
        statusKind: `contract`,
        known: false,
        unavailable,
      }
    }
    if (status.application == null || status.actions.editFinancing.allowed) {
      return { ...UNLOCKED, statusKind: `contract`, unavailable }
    }
    return {
      locked: true,
      pending: false,
      reason:
        status.actions.editFinancing.reason ?? BATTERY_FINANCING_LOCKED_MESSAGE,
      statusKind: `contract`,
      known: true,
      unavailable,
    }
  }

  const financing = financingQuery.data
  const unavailable = financingQuery.isError
  if (financing == null) {
    return {
      locked: false,
      pending: holdsWhileLoading && !financingQuery.isError,
      reason: null,
      statusKind: `financing-status`,
      known: false,
      unavailable,
    }
  }
  if (financing.financingLocked === true) {
    return {
      locked: true,
      pending: false,
      reason: financing.financingLockReason ?? SUNGAGE_FINANCING_LOCKED_REASON,
      statusKind: `financing-status`,
      known: true,
      unavailable,
    }
  }
  if (financing.currentApplication?.editLocked === true) {
    return {
      locked: true,
      pending: false,
      reason: SUNGAGE_FINANCING_LOCKED_REASON,
      statusKind: `financing-status`,
      known: true,
      unavailable,
    }
  }
  return { ...UNLOCKED, unavailable }
}
